import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useOutletContext } from 'react-router'

import { PageLevelTableWrapper, TOAST_TYPE } from '@base'
import { Button, Table as TableV2, useTableReducer } from '@baseV2'
import { ShipmentFilters, strings } from '@constants'
import { getAllShipmentsByRMApi } from '@data'
import { showToast } from '@data/state/action'
import { paths } from '@routes'
import { formatPeriod } from '@utils'
import StatusChip from '@pages/Admin/Orders/ManageOrder/Shipment/StatusChip'
import { displayItemWeight } from '../AddDispatchPlanV2'
import { CopySpan } from '../style'

const twoLine = (primary, secondary) => (
	<div>
		<div>{primary ?? '-'}</div>
		<small>
			<b>{secondary ?? '-'}</b>
		</small>
	</div>
)

const ShipmentList = () => {
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const [state, tableDispatch, actions] = useTableReducer(ShipmentFilters)
	const { forceUpdate } = useOutletContext()
	const userId = useSelector((_state) => _state.getIn(['user', 'user', 'user_id']))

	useEffect(() => {
		if (state.loading) {
			return
		}
		tableDispatch(actions.fetchTableDataProgress())
		getAllShipmentsByRMApi(userId, state.query.page, 10, state.query.filters)
			.then((response) => {
				tableDispatch(actions.fetchTableDataSuccess(response.data, response.count))
			})
			.catch((error) => tableDispatch(actions.fetchTableDataFailure(error)))
	}, [state.query, forceUpdate])

	const openShipment = (row) => navigate(paths.admin_manage_enquiry_order_edit_fulfillment_edit(row?.order_id, row?.shipment_request_id))

	const copyOrderId = (e, id) => {
		if (!id) {
			return
		}
		// the row itself navigates, so keep the click here
		e.stopPropagation()
		navigator.clipboard.writeText(id)
		dispatch(showToast(true, strings(['order', 'id', 'copied']), { type: TOAST_TYPE.INFO }))
	}

	const columns = useMemo(
		() =>
			[
				{
					label: `${strings(['order', 'id'])} / ${strings(['shipment', 'id'])}`,
					key: (data) => data,
					renderer: (data) => twoLine(<CopySpan onClick={(e) => copyOrderId(e, data?.order_id)}>{data?.order_id ?? '-'}</CopySpan>, data?.shipment_request_id),
					minWidth: 115,
					mobile: true,
				},
				{
					label: `${strings('buyer')} / ${strings(['shipping', 'location'])}`,
					key: (data) => data,
					renderer: (data) => twoLine(
							data?.buyer_name,
							[data?.buyer_shipping_location?.city, data?.buyer_shipping_location?.state].filter(Boolean).join(', ') || '-'
						),
					minWidth: 140,
					mobile: true,
				},
				{
					label: `${strings('invoiced')} / ${strings(['ordered', 'quantity'])}`,
					key: (data) => data,
					renderer: (data) => (
						<div>
							{displayItemWeight(data?.serviced_weight, data?.input_type)} / {displayItemWeight(data?.required_weight, data?.input_type)}
						</div>
					),
					minWidth: 120,
					mobile: true,
				},
				{
					label: [strings('shipment'), strings('fulfillment'), strings('approved'), strings(['dispatch', 'planned'])].join(' | '),
					key: (data) => data,
					renderer: (data) =>
						[data?.shipment_request_weight, data?.fulfillment_approved_weight, data?.approved_weight, data?.dispatch_planned_weight]
							.map((weight) => displayItemWeight(weight, data?.input_type))
							.join(' | '),
					minWidth: 220,
					mobile: true,
				},
				{
					label: strings(['delivery', 'date']),
					key: (data) => data,
					renderer: (data) => <div>{formatPeriod(data?.from_date, data?.to_date)}</div>,
					minWidth: 160,
					mobile: true,
				},
				{ label: strings(['sales', 'person']), key: (data) => data?.relationship_managers?.[0], default: '-', minWidth: 100, mobile: true },
				{
					label: strings('status'),
					key: 'status',
					renderer: (data) => <StatusChip status={data} />,
					minWidth: 90,
					mobile: true,
				},
				{
					label: 'Actions',
					key: (data) => data,
					renderer: (data) => (
						<Button
							type='button'
							variant='outline'
							color='primary'
							size='small'
							onClick={(e) => {
								// the row itself opens the same page
								e.stopPropagation()
								openShipment(data)
							}}
						>
							{strings('check_fulfillment')}
						</Button>
					),
					minWidth: 150,
					mobile: true,
					align: 'right',
				},
				// Paginated table: client-side sort would only reorder the current page, so keep all columns non-sortable.
			].map((col) => ({ ...col, sortable: false })),
		[]
	)

	const onPageChangeHandler = (next = true) => {
		tableDispatch(actions.changePage(next))
	}

	return (
		<PageLevelTableWrapper>
			<TableV2
				data={state.data}
				columns={columns}
				loading={state.loading}
				rowsToDisplay={10}
				page={state.query.page}
				totalItemCount={state.total_count}
				onPageChange={onPageChangeHandler}
				onRowClick={openShipment}
				fullHeight
			/>
		</PageLevelTableWrapper>
	)
}

export default ShipmentList
